import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ExternalLink, FileSpreadsheet, ImageDown, Plus } from "lucide-react";
import { PageHeader } from "../../components/PageHeader";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import { useIsPhone } from "../../hooks/useIsPhone";
import { DataTable, type Column } from "../../components/DataTable";
import { TwoStageFilter } from "../../components/TwoStageFilter";
import { HubSpotLinkCell } from "../../components/HubSpotLinkCell";
import {
  HubSpotMatchDialog,
  type HubSpotMatchTarget,
} from "../../components/HubSpotMatchDialog";
import { useAllPersons } from "../../hooks/useAllPersons";
import { useAllHubSpotLinks } from "../../hooks/useHubSpot";
import { FILTER_ROLES, titleCase } from "../../utils/filterOptions";
import {
  isSharePointConfigured,
  getUsersDirectoryUrl,
} from "../../utils/sharepoint";
import { isHubSpotConfigured } from "../../utils/hubspot";
import { ExcelUserImportDialog } from "./ExcelUserImportDialog";
import { HeadshotPullDialog } from "./HeadshotPullDialog";
import { CodeRequestsBanner } from "./CodeRequestsBanner";
import {
  dismissCodeRequest,
  fetchPendingCodeRequests,
  type CodeRequestDoc,
} from "../../services/codeRequestService";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { formatDirectoryName } from "@event-planner/shared";
import type { Person, Role } from "@event-planner/shared";

type RowStatus = "active" | "pending";

export interface UserRow {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  honorificPrefix: string | null;
  honorificSuffix: string | null;
  email: string;
  role: Role | null;
  status: RowStatus;
  photoURL: string | null;
}

/**
 * Build the directory rows from the `persons` collection. Each Person doc
 * is one row; status is derived from `authUid` (null => pending, set =>
 * active). Replaces the prior `buildUserRows` merge of `users` +
 * `contacts` by-email, which was a workaround for the dual-collection
 * model retired in ADR-136.
 *
 * Pure, exported for unit testing.
 */
export function buildPersonRows(persons: Person[]): UserRow[] {
  return persons.map((p) => ({
    id: p.id,
    firstName: p.firstName ?? "",
    lastName: p.lastName ?? "",
    displayName: p.displayName || p.email,
    honorificPrefix: p.honorificPrefix,
    honorificSuffix: p.honorificSuffix,
    email: p.email,
    role: p.role,
    status: p.authUid === null ? "pending" : "active",
    photoURL: p.photoURL,
  }));
}

type FilterMode = "none" | "role" | "status";

interface FilterState {
  mode: FilterMode;
  role: Role | null;
  status: RowStatus | null;
}

const INITIAL_FILTER: FilterState = {
  mode: "none",
  role: null,
  status: null,
};

export function UserListPage() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const { data: persons, loading, refetch: refetchPersons } = useAllPersons();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [headshotPullOpen, setHeadshotPullOpen] = useState(false);
  const [codeRequests, setCodeRequests] = useState<CodeRequestDoc[]>([]);
  const [hubspotMatchTarget, setHubspotMatchTarget] =
    useState<HubSpotMatchTarget | null>(null);
  const {
    links: hubspotLinks,
    loading: hubspotLinksLoading,
    error: hubspotLinksError,
    refetch: refetchHubspotLinks,
  } = useAllHubSpotLinks(isHubSpotConfigured());

  // Pending code-request rows. Read once on mount, refetched after a create
  // flow completes so the banner clears when matching emails are claimed.
  const fetchCodeRequests = useCallback(async () => {
    try {
      setCodeRequests(await fetchPendingCodeRequests());
    } catch {
      // non-fatal, banner just stays empty
    }
  }, []);

  useEffect(() => {
    fetchCodeRequests();
  }, [fetchCodeRequests]);

  // Legacy deep-link compatibility: /users?createOpen=1&prefillEmail=...
  // used to open the CreateUserDialog inline. Post-cleanup the create
  // surface is the dedicated /users/new page, so we redirect any inbound
  // legacy URL there with the prefill params preserved.
  useEffect(() => {
    if (searchParams.get("createOpen") === "1") {
      const next = new URLSearchParams();
      const prefillEmail = searchParams.get("prefillEmail");
      const prefillEventId = searchParams.get("prefillEventId");
      if (prefillEmail) next.set("prefillEmail", prefillEmail);
      if (prefillEventId) next.set("prefillEventId", prefillEventId);
      navigate(`/users/new?${next.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const allRows: UserRow[] = useMemo(
    () => buildPersonRows(persons),
    [persons],
  );

  // Apply filters
  const filteredRows = useMemo(() => {
    let result = allRows;

    if (filter.mode === "role" && filter.role) {
      result = result.filter((r) => r.role === filter.role);
    }
    if (filter.mode === "status" && filter.status) {
      result = result.filter((r) => r.status === filter.status);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.displayName.toLowerCase().includes(q) ||
          r.firstName.toLowerCase().includes(q) ||
          r.lastName.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q),
      );
    }

    // Sort by lastName, then firstName
    return result.sort((a, b) => {
      const last = a.lastName.localeCompare(b.lastName);
      if (last !== 0) return last;
      return a.firstName.localeCompare(b.firstName);
    });
  }, [allRows, filter, search]);

  function handleRowClick(row: UserRow) {
    navigate(`/users/${row.id}`);
  }

  function handleCreateFromRequest(email: string) {
    navigate(`/users/new?prefillEmail=${encodeURIComponent(email)}`);
  }

  // Non-sticky: the row leaves the banner but stays in Firestore as an
  // audit record, and the same email can request again later (ADR-170).
  async function handleDismissRequest(request: CodeRequestDoc) {
    try {
      await dismissCodeRequest(request.id, user?.uid ?? "unknown");
      showSuccess(`Dismissed request from ${request.email}.`);
      await fetchCodeRequests();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to dismiss the request.",
      );
    }
  }

  const columns: Column<UserRow>[] = [
    {
      id: "name",
      label: "Name",
      render: (row) => {
        const directoryName = formatDirectoryName({
          honorificPrefix: row.honorificPrefix,
          firstName: row.firstName,
          lastName: row.lastName,
          honorificSuffix: row.honorificSuffix,
          fallback: row.displayName || row.email,
        });
        return (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={row.photoURL ?? undefined}
              alt={row.displayName}
              sx={{ width: 34, height: 34 }}
            >
              {row.firstName?.[0] || row.displayName[0] || "?"}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: "0.938rem", fontWeight: 500, lineHeight: 1.3 }}>
                {directoryName}
              </Typography>
              {row.email && (
                <Typography sx={{ fontSize: "0.813rem", color: "text.secondary", lineHeight: 1.3 }}>
                  {row.email}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      },
    },
    ...(isHubSpotConfigured()
      ? ([
          {
            id: "hubspot",
            label: "HubSpot",
            width: "96px",
            phone: "chips",
            align: "center",
            render: (row) => (
              <HubSpotLinkCell
                personId={row.id}
                email={row.email}
                displayName={row.displayName}
                contactId={hubspotLinks.get(row.id)}
                loading={hubspotLinksLoading}
                error={hubspotLinksError}
                onRetry={refetchHubspotLinks}
                onOpenMatch={() =>
                  setHubspotMatchTarget({
                    personId: row.id,
                    email: row.email,
                    displayName: row.displayName,
                  })
                }
              />
            ),
          },
        ] satisfies Column<UserRow>[])
      : []),
    {
      id: "role",
      label: "Role",
      width: "120px",
      phone: "chips",
      render: (row) =>
        row.role ? (
          <Chip
            label={row.role}
            size="small"
            sx={{ textTransform: "capitalize", fontSize: "0.813rem" }}
          />
        ) : (
          <Typography color="text.disabled" sx={{ fontSize: "0.875rem" }}>—</Typography>
        ),
    },
    {
      id: "status",
      label: "Status",
      width: "120px",
      phone: "chips",
      render: (row) =>
        row.status === "active" ? (
          <Tooltip title="Person has an authenticated account" arrow>
            <Chip
              label="Active"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: "0.813rem" }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Person has not yet authenticated" arrow>
            <Chip
              label="Pending"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: "0.813rem" }}
            />
          </Tooltip>
        ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Users"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("/users/new")}
                  data-testid="new-user-button"
                >
                  New User
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <FileSpreadsheet size={18} />,
                  onClick: () => setExcelImportOpen(true),
                  testId: "import-users-excel-button",
                },
                {
                  label: "Pull headshots",
                  icon: <ImageDown size={18} />,
                  onClick: () => setHeadshotPullOpen(true),
                  testId: "pull-headshots-button",
                },
                ...(isSharePointConfigured()
                  ? [
                      {
                        label: "Open in SharePoint",
                        icon: <ExternalLink size={18} />,
                        onClick: () => window.open(getUsersDirectoryUrl(), "_blank"),
                        testId: "open-sharepoint-users-button",
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
          <Stack direction="row" spacing={1}>
            {isSharePointConfigured() && (
              <Tooltip title="Open the Users Directory in SharePoint">
                <Button
                  variant="outlined"
                  startIcon={<ExternalLink size={18} />}
                  onClick={() => window.open(getUsersDirectoryUrl(), "_blank")}
                  data-testid="open-sharepoint-users-button"
                >
                  Open in SharePoint
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={18} />}
              onClick={() => setExcelImportOpen(true)}
              data-testid="import-users-excel-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImageDown size={18} />}
              onClick={() => setHeadshotPullOpen(true)}
              data-testid="pull-headshots-button"
            >
              Pull headshots
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("/users/new")}
              data-testid="new-user-button"
            >
              New User
            </Button>
          </Stack>
          )
        }
      />

      <CodeRequestsBanner
        codeRequests={codeRequests}
        onCreateFromRequest={handleCreateFromRequest}
        onDismissRequest={handleDismissRequest}
      />

      <TwoStageFilter<Role | RowStatus>
        search={search}
        onSearchChange={setSearch}
        searchTestId="user-search-input"
        noneLabel="All users"
        modes={[
          {
            value: "role",
            label: "Filter by role",
            placeholder: "Select role…",
            options: FILTER_ROLES.map((r) => ({ value: r, label: titleCase(r) })),
          },
          {
            value: "status",
            label: "Filter by account status",
            placeholder: "Select status…",
            options: [
              { value: "active", label: "Active" },
              { value: "pending", label: "Pending" },
            ],
          },
        ]}
        mode={filter.mode}
        value={filter.mode === "role" ? filter.role : filter.status}
        onChange={({ mode, value }) => {
          const m = mode as FilterMode;
          if (m === "role") {
            setFilter({ mode: m, role: (value as Role) ?? null, status: null });
          } else if (m === "status") {
            setFilter({
              mode: m,
              role: null,
              status: (value as RowStatus) ?? null,
            });
          } else {
            setFilter(INITIAL_FILTER);
          }
        }}
        modeTestId="user-filter-mode-select"
        valueTestId="user-filter-value-select"
      />

      <DataTable
        columns={columns}
        data={filteredRows}
        loading={loading}
        emptyMessage="No users found."
        ariaLabel="Users table"
        getRowKey={(row) => row.id}
        onRowClick={handleRowClick}
      />

      <ExcelUserImportDialog
        open={excelImportOpen}
        onClose={() => setExcelImportOpen(false)}
        onImported={() => {
          refetchPersons();
        }}
      />

      <HeadshotPullDialog
        open={headshotPullOpen}
        onClose={() => setHeadshotPullOpen(false)}
        onImported={() => {
          refetchPersons();
        }}
      />

      <HubSpotMatchDialog
        target={hubspotMatchTarget}
        onClose={() => setHubspotMatchTarget(null)}
        onConfirm={(suggestion) => {
          // Review happens on the person's edit form — hand the picked
          // contact over via the query param (ADR-172).
          if (!hubspotMatchTarget) return;
          navigate(
            `/users/${hubspotMatchTarget.personId}?hubspotContact=${suggestion.contactId}`,
          );
        }}
      />
    </Box>
  );
}
